import React, { useEffect, useState } from 'react';
import StripedProgressBar from '../_common/StripedProgressBar';
import { PolarisWebSocket, useWebSocket } from '@/contextAPI/websocketsContext';
import { useAppDispatch } from '@/redux/hooks';
import { getData } from '@/redux/slices/dataTableSlice';
import { OrderJob, Order } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import PolarisIcon from '../_common/PolarisIcon';
import LinearPercentage from '../_common/LinearPercentage';

interface BulkOrdersProgressBarProps {
  order: Order;
}

const BulkOrdersProgressBar = ({ order }: BulkOrdersProgressBarProps) => {
  const dispatch = useAppDispatch();
  const [orderSocket, setOrderSocket] = useState<null | PolarisWebSocket>(null);
  const { createSocket, removeSocket } = useWebSocket();
  const [jobStats, setJobStats] = useState<Order['job_stats'] | null>(order?.job_stats);

  useEffect(() => {
    setJobStats(order.job_stats);
    const { job_stats } = order || {};
    const { out_of_sync, requested_jobs } = job_stats;
    if (out_of_sync || !requested_jobs) return;

    if (order.status === 'bulk_processing') {
      const { id, hid } = order;
      // only open a socket if it's not already open
      if (!orderSocket) {
        setOrderSocket(
          createSocket(
            hid,
            `/ws/orders/${id}/updates/`,
            (update: any) => {
              const data: { status: string; job?: OrderJob; job_stats: Order['job_stats'] } = update.message.message;
              setJobStats(data.job_stats);
              if (data.job_stats.total_jobs + data.job_stats.bulk_errors >= data.job_stats.requested_jobs) {
                console.log('closing socket because total_jobs + bulk_errors >= requested_jobs');
                removeSocket(order.hid);
                dispatch(getData());
              }
            },
            () => dispatch(getData())
          )
        );
      } else if (order.status === 'created' && !!orderSocket?.socket) {
        console.log('closing socket because order.status === created');
        removeSocket(order.hid);
      }
    }
  }, [order]);

  const {
    requested_jobs = 0,
    total_jobs = 0,
    complete = 0,
    in_error = 0,
    failed = 0,
    cancelled = 0,
    bulk_errors = 0,
    created = 0,
    in_progress = 0,
    submitting = 0
  } = jobStats;

  if (!requested_jobs) {
    return <LinearPercentage total={0} tooltipText="There was a problem retrieving job stats." />;
  }

  return (
    <ProgressBarWrapper>
      <StripedProgressBar
        isProcessing
        total={requested_jobs}
        pending={in_progress + submitting}
        completed={complete}
        error={in_error + failed}
        cancelled={cancelled + created}
        tooltipText={`${total_jobs.toLocaleString()} of ${requested_jobs.toLocaleString()} job${requested_jobs !== 1 ? 's' : ''} processed`}
      />
      {bulk_errors > 0 && (
        <Tooltip title={`${bulk_errors} error${bulk_errors !== 1 ? 's' : ''}`} arrow>
          <StyledPolarisIcon icon="error_filled" height={20} />
        </Tooltip>
      )}
    </ProgressBarWrapper>
  );
};

export default BulkOrdersProgressBar;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
`;

const StyledPolarisIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  margin-left: ${({ theme }) => theme.spacing(1)};
`;
